(() => {
  const escapeSelector = (value) => window.CSS?.escape ? CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');

  const initializeGallery = (gallery) => {
    if (gallery.dataset.ctDotsInitialized === 'true') return;
    const scroller = gallery.querySelector('[data-ct-mobile-gallery-scroller]');
    const slides = Array.from(scroller?.querySelectorAll('.product__media-item') || []);
    const dots = Array.from(gallery.querySelectorAll('[data-ct-mobile-gallery-dot]'));
    const dotsContainer = gallery.querySelector('.ct-product-media-mobile-dots');
    if (!scroller || slides.length < 2 || !dots.length) return;
    gallery.dataset.ctDotsInitialized = 'true';
    let frame;
    const positionDots = (activeIndex = 0) => {
      const media = slides[activeIndex]?.querySelector('.product-media-container') || slides[activeIndex];
      const viewer = gallery.querySelector('.ct-product-gallery-viewer');
      if (!media || !viewer || !dotsContainer) return;
      const mediaRect = media.getBoundingClientRect();
      const viewerRect = viewer.getBoundingClientRect();
      dotsContainer.style.setProperty('--ct-gallery-dots-top', `${mediaRect.bottom - viewerRect.top - 24}px`);
    };
    const updateDots = () => {
      const activeIndex = slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - scroller.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
        dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });
      positionDots(activeIndex);
    };
    scroller.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateDots);
    }, { passive: true });
    dots.forEach((dot, index) => dot.addEventListener('click', () => {
      slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }));
    window.addEventListener('resize', updateDots, { passive: true });
    gallery.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', updateDots, { once: true });
    });
    updateDots();
  };

  const initializeBar = (bar) => {
    if (bar.dataset.initialized === 'true') return;
    bar.dataset.initialized = 'true';
    const productInfo = bar.closest('product-info');
    const purchaseArea = productInfo?.querySelector('.ct-product-purchase');
    const addButton = bar.querySelector('[data-sticky-add]');
    const modal = bar.querySelector('[data-sticky-dialog]');
    const stickyQuantity = bar.querySelector('[data-sticky-quantity]');
    const price = bar.querySelector('[data-sticky-price]');
    let unitPriceCents = Number(price?.dataset.unitPriceCents || 0);
    let syncingQuantity = false;
    let purchaseIsIntersecting = false;

    const getMainForm = () => productInfo?.querySelector('.ct-product-purchase product-form form[data-type="add-to-cart-form"]');
    const getMainQuantity = () => productInfo?.querySelector('.ct-product-purchase input[name="quantity"]');
    const getMainVariantId = () => getMainForm()?.querySelector('input[name="id"]')?.value;

    const formatPrice = (cents) => {
      const format = bar.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount_with_comma_separator';
      const noDecimals = placeholder.includes('no_decimals');
      const commaDecimal = placeholder.includes('comma_separator');
      const decimalSeparator = commaDecimal ? ',' : '.';
      const thousandsSeparator = commaDecimal ? '.' : ',';
      const amount = (Number(cents) / 100).toFixed(noDecimals ? 0 : 2);
      const parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
      const formattedAmount = noDecimals ? parts[0] : `${parts[0]}${decimalSeparator}${parts[1]}`;
      return format.replace(/\{\{\s*\w+\s*\}\}/, formattedAmount);
    };

    const updateTotalPrice = () => {
      if (!price) return;
      const quantity = Math.max(1, Number(stickyQuantity?.value || getMainQuantity()?.value || 1));
      price.textContent = formatPrice(unitPriceCents * quantity);
    };

    const copyQuantityRules = () => {
      const mainQuantity = getMainQuantity();
      if (!mainQuantity || !stickyQuantity) return;
      ['min', 'max', 'step'].forEach((attribute) => {
        if (mainQuantity.hasAttribute(attribute)) stickyQuantity.setAttribute(attribute, mainQuantity.getAttribute(attribute));
        else stickyQuantity.removeAttribute(attribute);
      });
    };

    const setQuantity = (source, target, notify) => {
      if (syncingQuantity || !source || !target || source.value === target.value) return;
      syncingQuantity = true;
      target.value = source.value;
      if (notify) target.dispatchEvent(new Event('change', { bubbles: true }));
      syncingQuantity = false;
    };

    const syncVariant = (variantId) => {
      if (!variantId) return;
      const matchingCard = modal?.querySelector(`[data-sticky-variant][data-variant-id="${escapeSelector(variantId)}"]`);
      if (!matchingCard) return;
      modal.querySelectorAll('[data-sticky-variant]').forEach((card) => {
        const selected = card === matchingCard;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      unitPriceCents = Number(matchingCard.dataset.variantPriceCents || unitPriceCents);
      updateTotalPrice();
      addButton.disabled = matchingCard.dataset.available !== 'true';
    };

    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.hidden = true;
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && modal && !modal.hidden) {
        closeModal();
        addButton?.focus();
      }
    };

    const openModal = () => {
      if (!modal) return;
      syncVariant(getMainVariantId());
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-open'));
      modal.querySelector('.is-selected:not([disabled]), [data-sticky-variant]:not([disabled])')?.focus();
    };

    const submitMainForm = () => {
      const mainForm = getMainForm();
      const submitButton = mainForm?.querySelector('[type="submit"]');
      if (!mainForm || submitButton?.disabled) return;
      setQuantity(stickyQuantity, getMainQuantity(), true);
      mainForm.requestSubmit(submitButton);
    };

    const updateVisibility = () => {
      bar.classList.toggle('is-hidden', purchaseIsIntersecting);
      bar.classList.add('is-ready');
      bar.style.visibility = purchaseIsIntersecting ? 'hidden' : 'visible';
    };

    addButton?.addEventListener('click', () => {
      if (bar.dataset.hasVariants === 'true') openModal();
      else submitMainForm();
    });
    document.addEventListener('keydown', handleEscape);

    const handleBarInteraction = (event) => {
      const closeButton = event.target.closest('[data-sticky-close]');
      const variantButton = event.target.closest('[data-sticky-variant]');
      if (closeButton) {
        closeModal();
        return;
      }
      if (!variantButton || variantButton.dataset.available !== 'true') return;
      const variantId = variantButton.dataset.variantId;
      const mainCard = productInfo?.querySelector(`.ct-product-variant-cards__card[data-variant-id="${escapeSelector(variantId)}"]`);
      if (!mainCard) return;
      syncVariant(variantId);
      mainCard.click();
      closeModal();
      window.setTimeout(() => {
        syncVariant(getMainVariantId() || variantId);
        copyQuantityRules();
        submitMainForm();
      }, 450);
    };
    bar.addEventListener('click', handleBarInteraction);
    if (modal) {
      modal.addEventListener('click', handleBarInteraction);
      document.body.appendChild(modal);
    }

    stickyQuantity?.addEventListener('input', () => {
      setQuantity(stickyQuantity, getMainQuantity(), false);
      updateTotalPrice();
    });
    stickyQuantity?.addEventListener('change', () => {
      setQuantity(stickyQuantity, getMainQuantity(), true);
      updateTotalPrice();
    });
    productInfo?.addEventListener('input', (event) => {
      if (event.target === getMainQuantity()) {
        setQuantity(event.target, stickyQuantity, false);
        updateTotalPrice();
      }
    });
    productInfo?.addEventListener('change', (event) => {
      if (event.target === getMainQuantity()) {
        setQuantity(event.target, stickyQuantity, false);
        updateTotalPrice();
      }
      if (event.target.name === 'id' && !bar.contains(event.target)) syncVariant(event.target.value);
      window.setTimeout(() => {
        syncVariant(getMainVariantId());
        copyQuantityRules();
        updateTotalPrice();
      }, 80);
    });
    productInfo?.addEventListener('click', (event) => {
      const mainCard = event.target.closest('.ct-product-variant-cards__card[data-variant-id]');
      if (mainCard && !bar.contains(mainCard)) syncVariant(mainCard.dataset.variantId);
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'product-form') return;
        const mainQuantity = getMainQuantity();
        const resetQuantity = mainQuantity?.getAttribute('min') || mainQuantity?.dataset.min || '1';

        if (mainQuantity) mainQuantity.value = resetQuantity;
        if (stickyQuantity) stickyQuantity.value = resetQuantity;

        copyQuantityRules();
        updateTotalPrice();
      });
    }

    if (purchaseArea) {
      const observer = new IntersectionObserver(([entry]) => {
        purchaseIsIntersecting = entry.isIntersecting;
        updateVisibility();
      }, { rootMargin: '0px', threshold: 0 });
      observer.observe(purchaseArea);
    } else {
      bar.classList.add('is-ready');
      bar.style.visibility = 'visible';
    }
    copyQuantityRules();
    setQuantity(getMainQuantity(), stickyQuantity, false);
    syncVariant(getMainVariantId());
    updateTotalPrice();
  };

  const syncGoogleReviewCount = () => {
    const reviewRoot = document.querySelector('[id*="shopify-block"][id*="gropulse" i], .shopify-app-block, [class*="gropulse" i]');
    if (!reviewRoot) return false;
    const matchingText = Array.from(reviewRoot.querySelectorAll('*')).map((node) => node.textContent?.trim()).find((value) => /(?:recensioni|reviews)\s*[:(]?\s*([0-9][0-9.,]*)|([0-9][0-9.,]*)\s*(?:recensioni|reviews)/i.test(value || ''));
    const count = matchingText?.match(/([0-9][0-9.,]*)/)?.[1];
    if (!count) return false;
    document.querySelectorAll('[data-google-review-count]').forEach((node) => { node.textContent = count; });
    return true;
  };

  const initialize = () => {
    document.querySelectorAll('ct-sticky-product-bar').forEach(initializeBar);
    document.querySelectorAll('media-gallery').forEach(initializeGallery);
    if (syncGoogleReviewCount()) return;
    const observer = new MutationObserver(() => {
      if (syncGoogleReviewCount()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 12000);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
